/**
 * GsePricingController on Skynet.Pricing (PPE 2.0 / DEV-4748) — the GSE-feed operational surface Margin
 * Center's Cockpit reads from:
 *
 * - GET /api/gse-pricing/freshness — Margin Center MC-4 (DEV-4966): Base/SRP feed age + staleness per
 *   Seller/PriceType.
 * - GET /api/gse-pricing/pulls?status=X — Margin Center MC-5 (DEV-4967): the operator review queue of
 *   durable pulls in the given GsePullStatus (Quarantined is Cockpit's use case).
 *
 * Company-scoped by JWT; gated behind FeedAdminPolicy.Access server-side — a caller without the claim
 * 403s. Every call here sends SKIP_FORBIDDEN, so that 403 reaches the calling action (an empty list or an
 * inline error) instead of triggering the global full-page reload.
 */
import { SKIP_FORBIDDEN } from './httpConfig';

// The pulls list keeps its JSON-array body; paging totals travel in these response headers (axios
// lower-cases header names).
const PAGING_HEADER = {
  TOTAL_COUNT: 'x-total-count',
  PAGE: 'x-page',
  PAGE_SIZE: 'x-page-size',
};

const readNumberHeader = (headers, name) => {
  const value = Number.parseInt(headers?.[name], 10);

  return Number.isNaN(value) ? null : value;
};

export class GsePricingApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getFreshness() {
    return this.#http.get('/api/gse-pricing/freshness', SKIP_FORBIDDEN);
  }

  // `reviewed` omitted hides held pulls a person already promoted or discarded; market-closed holds are
  // left out unless `includeMarketClosed` is set. The totals are null when the headers are not readable
  // (e.g. not exposed to the browser), so callers can fall back to the page they got.
  async getPulls({ status, reviewed, includeMarketClosed, page, pageSize } = {}) {
    const response = await this.#http.get('/api/gse-pricing/pulls', {
      ...SKIP_FORBIDDEN,
      params: { status, reviewed, includeMarketClosed, page, pageSize },
    });

    return {
      data: response?.data,
      totalCount: readNumberHeader(response?.headers, PAGING_HEADER.TOTAL_COUNT),
      page: readNumberHeader(response?.headers, PAGING_HEADER.PAGE),
      pageSize: readNumberHeader(response?.headers, PAGING_HEADER.PAGE_SIZE),
    };
  }

  // A person's release of a held pull: promote serves it now, discard keeps it out of pricing for
  // good. A 409 carries the reason the guard refused it.
  async promotePull(gsePricingPullId, { note = null } = {}) {
    return this.#http.post(
      `/api/gse-pricing/pulls/${gsePricingPullId}/promote`,
      { note },
      SKIP_FORBIDDEN,
    );
  }

  async discardPull(gsePricingPullId, { note = null } = {}) {
    return this.#http.post(
      `/api/gse-pricing/pulls/${gsePricingPullId}/discard`,
      { note },
      SKIP_FORBIDDEN,
    );
  }

  // The company's soft anomaly bands: defaults, overrides, effective values, allowed ranges and the
  // hard limits. The PUT replaces every override (null = use the default); out of range is a 400.
  async getAnomalyThresholds() {
    return this.#http.get('/api/gse-pricing/anomaly-thresholds', SKIP_FORBIDDEN);
  }

  async updateAnomalyThresholds(overrides) {
    return this.#http.put('/api/gse-pricing/anomaly-thresholds', overrides, SKIP_FORBIDDEN);
  }

  // LEN-88: the priced grid a single durable pull produced (the Feeds & Sources "Recent pulls"
  // drill-down). A Cash pull returns its asset-price grid cells; an SRP-grid feed or a pull whose raw
  // payload is gone returns hasGrid=false + a note; another tenant's pull is a 404.
  async getPullGrid(gsePricingPullId) {
    return this.#http.get(`/api/gse-pricing/pulls/${gsePricingPullId}/grid`, SKIP_FORBIDDEN);
  }

  // LEN-105: an SRP-feed pull as a note-rate x loan-amount matrix per servicer for one product /
  // state / occupancy (GET gse-pricing/pulls/{id}/srp-matrix). Every filter is optional server-side.
  async getPullSrpMatrix(
    gsePricingPullId,
    { product, state, occupancy, minNoteRate, maxNoteRate } = {},
  ) {
    return this.#http.get(`/api/gse-pricing/pulls/${gsePricingPullId}/srp-matrix`, {
      ...SKIP_FORBIDDEN,
      params: { product, state, occupancy, minNoteRate, maxNoteRate },
    });
  }

  // One Freddie product x execution ({commitment}:{servicingOption}) per call; both optional server-side.
  async getPullSpecifiedPools(gsePricingPullId, { product, execution } = {}) {
    return this.#http.get(`/api/gse-pricing/pulls/${gsePricingPullId}/specified-pools`, {
      ...SKIP_FORBIDDEN,
      params: { product, execution },
    });
  }

  // The Freddie source-product catalog behind the program editor's Execution Sources
  // picker. `seller` is an optional operator override — omitted, the company's resolved Loan
  // Selling Advisor seller id is used server-side.
  async getProgramCatalog({ seller } = {}) {
    return this.#http.get('/api/gse-pricing/program-catalog', {
      ...SKIP_FORBIDDEN,
      params: { seller },
    });
  }

  // DEV-5601: the per-seller Freddie execution-leg enablement editor (commitment × servicing).
  // GET returns { seller, enabledKeys, catalog[] } — each catalog leg carrying its supportStatus
  // (only Live legs may be enabled). `seller` is the same optional operator override as the catalog
  // read. Skips the global forbidden-reload so a missing gsePricingFeedAdmin.access claim surfaces
  // inline rather than triggering a full-page reload.
  async getExecutions({ seller } = {}) {
    return this.#http.get('/api/gse-pricing/executions', { ...SKIP_FORBIDDEN, params: { seller } });
  }
}
